/**
 * Composant d'édition IA des documents générés
 * Permet de modifier les documents avec des instructions en langage naturel
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, History, Sparkles, Loader } from 'lucide-react';
import { chatCompletions } from '../services/openaiBackend';

interface AIDocumentEditorProps {
  isOpen: boolean;
  onClose: () => void;
  documentType: 'cahier' | 'roadmap' | 'plan-directeur' | 'ods' | 'bilan' | 'audit';
  currentContent: string;
  onContentUpdate: (newContent: string) => void;
  companyName: string;
}

interface HistoryEntry {
  content: string;
  timestamp: Date;
  instruction: string;
}

// Historiques séparés par type de document (persiste pendant la session)
const documentHistories: Record<string, HistoryEntry[]> = {
  'cahier': [],
  'roadmap': [],
  'plan-directeur': [],
  'ods': [],
  'bilan': [],
  'audit': []
};

export default function AIDocumentEditor({
  isOpen,
  onClose,
  documentType,
  currentContent,
  onContentUpdate,
  companyName
}: AIDocumentEditorProps) {
  const [instruction, setInstruction] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Historique spécifique au document actuel
  const currentHistory = documentHistories[documentType] || [];

  const documentNames = {
    'cahier': 'Spécifications d\'exigences d\'affaires et TI',
    'roadmap': 'Roadmap Annuel',
    'plan-directeur': 'Plan Directeur',
    'ods': 'Offre de Service',
    'bilan': 'Bilan de santé TI',
    'audit': 'Audit de sécurité'
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!instruction.trim() || isProcessing) return;

    setIsProcessing(true);

    try {
      // Sauvegarder la version actuelle dans l'historique DU DOCUMENT SPÉCIFIQUE
      documentHistories[documentType] = [
        ...documentHistories[documentType],
        {
          content: currentContent,
          timestamp: new Date(),
          instruction: instruction
        }
      ];

      const systemContent = `Tu es un expert en rédaction de documents d'affaires pour ${companyName}.

⚠️ RÈGLES ABSOLUES - NE PAS DÉVIER:
1. Le document "${documentNames[documentType]}" est déjà EXCELLENT (90% parfait)
2. NE REFAIS JAMAIS le document depuis zéro - c'est INTERDIT
3. COPIE EXACTEMENT tout le document original
4. Fais UNIQUEMENT la modification spécifique demandée
5. GARDE tous les titres, sections, graphiques, tableaux, listes EXACTEMENT comme ils sont
6. Si la modification concerne "mois", change UNIQUEMENT les mentions de "mois" par "Q1 Année X", etc.
7. TOUT LE RESTE reste 100% identique

⚠️ IMPORTANT: Tu dois retourner le document ENTIER ET COMPLET, pas un résumé.

Ton rôle: Chirurgien de précision - modification ciblée uniquement.`;

      const modifiedContent = (await chatCompletions({
        model: 'gpt-4o',
        messages: [
          { role: 'system', content: systemContent },
          {
            role: 'user',
            content: `DOCUMENT ORIGINAL COMPLET (à conserver intégralement):\n\n${currentContent}\n\n━━━━━━━━━━━━━━━━━━\n\nMODIFICATION CIBLÉE (tout le reste doit rester EXACTEMENT pareil):\n${instruction}\n\n━━━━━━━━━━━━━━━━━━\n\nDOCUMENT COMPLET avec modification intégrée:`,
          },
        ],
        temperature: 0.2,
        max_tokens: 16000,
      })).trim();

      // Mettre à jour le document
      onContentUpdate(modifiedContent);
      
      // Réinitialiser l'instruction
      setInstruction('');

      console.log('Modification réussie !');

    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      alert(`Une erreur est survenue lors de la modification:\n\n${errorMessage}\n\nVeuillez réessayer.`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUndo = () => {
    const docHistory = documentHistories[documentType];
    if (docHistory.length === 0) return;

    // Restaurer la dernière version de CE document spécifique
    const lastVersion = docHistory[docHistory.length - 1];
    onContentUpdate(lastVersion.content);
    
    // Retirer cette version de l'historique
    documentHistories[documentType] = docHistory.slice(0, -1);
    
    // Force un re-render pour mettre à jour le compteur
    setShowHistory(prev => prev);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6" />
              <div>
                <h2 className="text-2xl font-bold">Éditeur IA</h2>
                <p className="text-purple-100 text-sm">{documentNames[documentType]}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-800 text-sm">
                💡 <strong>Exemples de modifications :</strong>
              </p>
              <ul className="text-blue-700 text-sm mt-2 space-y-1 ml-4 list-disc">
                <li>"Ajoute un 6e axe : Programmation et automatisation"</li>
                <li>"Rends le ton plus formel et professionnel"</li>
                <li>"Ajoute plus de détails sur la gouvernance"</li>
                <li>"Change la durée du projet à 9 mois"</li>
              </ul>
            </div>

            {/* History button - spécifique au document actuel */}
            {currentHistory.length > 0 && (
              <div className="mb-4">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm transition-colors"
                >
                  <History className="w-4 h-4" />
                  {currentHistory.length} modification{currentHistory.length > 1 ? 's' : ''} précédente{currentHistory.length > 1 ? 's' : ''} 
                  <span className="text-purple-600 font-semibold">({documentNames[documentType]})</span>
                </button>

                {showHistory && (
                  <div className="mt-3 space-y-2">
                    {currentHistory.map((entry, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 rounded-lg p-3 text-sm"
                      >
                        <div className="text-gray-600 text-xs mb-1">
                          {entry.timestamp.toLocaleTimeString('fr-FR')}
                        </div>
                        <div className="text-gray-900">{entry.instruction}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Chat input */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quelle modification veux-tu apporter ?
                </label>
                <textarea
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                  placeholder="Ex: Ajoute un 6e axe stratégique nommé 'Programmation' avec des détails sur l'automatisation..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  rows={4}
                  disabled={isProcessing}
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={!instruction.trim() || isProcessing}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {isProcessing ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Modification en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Modifier le document
                    </>
                  )}
                </button>

                {currentHistory.length > 0 && (
                  <button
                    type="button"
                    onClick={handleUndo}
                    className="flex items-center gap-2 bg-gray-200 text-gray-700 px-4 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                    disabled={isProcessing}
                    title={`Restaurer la version précédente du ${documentNames[documentType]}`}
                  >
                    ↶ Annuler
                  </button>
                )}
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

